import { Box } from '@lifeomic/chroma-react/components/Box';
import { Popover } from '@lifeomic/chroma-react/components/Popover';
import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  FileText,
  Helix,
  CheckList,
  User,
  Login,
  PlusCircle,
  Paperclip,
  Search,
  Trash,
  AlertTriangle,
} from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Text } from '@lifeomic/chroma-react/components/Text';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { useAttachmentArbiter } from '../AttachmentArbiter';
import { Skeleton } from '@lifeomic/chroma-react/components/Skeleton';
import {
  Attachment,
  DocumentReferenceAttachment,
  isDocumentReference,
  isFhirReference,
  isObservation,
  ObservationAttachment,
  PAGE_NUMBER_EXTENSION_URL,
  ReferenceOrResource,
  TemporaryAttachment,
} from '../AttachmentArbiter/Attachment';
import _uniqBy from 'lodash/uniqBy';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { lighten } from '@mui/material/styles';
import {
  type KnowledgeBaseCitation,
  parseKnowledgeBaseCitation,
} from '../../../../../util/knowledgeBaseCitation';
import {
  useOpenKnowledgeBaseFile,
  type OpenCsvKnowledgeBaseFile,
} from './useOpenKnowledgeBaseFile';
import { CsvAttachmentModal } from './CsvAttachmentViewer';

type AttachmentListProps = {
  activeImpression: ImpressionItem;
  attachments: fhir.Reference[];
  maxItemWidth?: number;
  action: 'readonly' | 'add' | 'read-reference';
  onUseAttachment: (attachment: ReferenceOrResource, index: number) => void;
  patient: fhir.Patient;
  fetchCarePlan: () => void;
};
import { Button } from '@lifeomic/chroma-react/components/Button';
import { SearchField } from '@lifeomic/chroma-react/components/SearchField';
import usePatientViewerFhirSearch from '../../../hooks/usePatientViewerFhirSearch';
import { DateTime } from 'luxon';
import { ImpressionItem } from '../../ExecutiveSummary/executive-summary';
import { DocumentReferenceContent } from '../AttachmentArbiter/modals/DocumentReferenceModal';
import { TextField } from '@lifeomic/chroma-react/components/TextField';
import { useFetchDimensionData } from '../../LabResults/hooks/useFetchDimensionData';

const ADD_ATTACHMENT_WIDTH = 73;
const PLUS_N_TEXT_WIDTH = 50;
const GAP_SIZE = 4;

const messages = defineMessages({
  extraAttachmentsCount: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.extraAttachmentsCount',
    defaultMessage: '+{count}',
  },
  additionalAttachments: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.additionalAttachments',
    defaultMessage: 'additional attachments',
  },
  patientRecordAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.patientRecordAttachment',
    defaultMessage: "{name}'s Patient Record",
  },
  documentReferencePage: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.documentReferencePage',
    defaultMessage: '(Page {pageNumber})',
  },
  insertAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.useAttachment',
    defaultMessage: 'Insert Attachment at cursor',
  },
  deleteAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.deleteAttachment',
    defaultMessage: 'Remove Attachment from this line',
  },
  viewAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.viewAttachment',
    defaultMessage: 'View Attachment',
  },
  addAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.addAttachment',
    defaultMessage: 'Add Attachment',
  },
  attachmentNotFound: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.attachmentNotFound',
    defaultMessage: 'Attachment not found',
  },
  pageNumber: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.attachmentList.pageNumber',
    defaultMessage: 'Page:',
  },
});

const useStyles = makeStyles<{
  maxItemWidth?: number;
  disableThumbnail?: boolean;
  action: 'readonly' | 'add' | 'edit' | 'read-reference';
}>(theme => ({
  container: {
    overflow: 'hidden',
    flexWrap: 'wrap',
    maxHeight: theme.pxToRem(34),
  },
  badge: ({ maxItemWidth, disableThumbnail, action }) => ({
    alignItems: 'center',
    background: theme.palette.common.white,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    flex: 1,
    flexShrink: 0,
    minHeight: theme.pxToRem(34),
    height: disableThumbnail ? theme.pxToRem(34) : 'auto',
    maxHeight: disableThumbnail ? theme.pxToRem(34) : theme.pxToRem(150),
    maxWidth:
      action === 'edit'
        ? 'unset'
        : maxItemWidth
        ? theme.pxToRem(maxItemWidth)
        : theme.spacing(35),
    overflow: 'hidden',
    padding: theme.spacing(0.5, 1),
    '& svg': {
      color: theme.palette.text.secondary,
      maxWidth: theme.pxToRem(20),
      minWidth: theme.pxToRem(20),
      strokeWidth: 1.75,
    },
    '&:hover, &:focus': {
      background: theme.palette.graphite[50],
      transition: '0.25s',
    },
  }),
  border: {
    border: `1px solid ${theme.palette.divider}`,
  },
  action: {
    '&$badge': {
      '& svg': {
        color: theme.palette.primary.main,
      },
      '&:hover, &:focus': {
        background: 'transparent',
      },
    },
  },
  visuallyHidden: {
    opacity: 0,
    visibility: 'hidden',
    cursor: 'default',
  },
  results: {
    overflowY: 'auto',
    flex: 1,
    padding: theme.spacing(1.5, 2.25),
  },
  emptyState: {
    flex: 1,
    color: lighten(theme.palette.text.hint, 0.25),
    minHeight: theme.pxToRem(125),
    paddingBottom: theme.spacing(1),
  },
  searchHeader: {
    backgroundColor: lighten(theme.palette.background.default, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexDirection: 'column',
    padding: theme.spacing(1.5),
  },
  popover: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: theme.pxToRem(300),
    overflowY: 'auto',
    width: theme.pxToRem(300),
  },
  attachmentItem: {
    flex: 0,
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    height: 'fit-content',
    width: '100%',
    '&:hover, &:focus, &:focus-visible': {
      backgroundColor: 'transparent',
    },
  },
  addAttachment: {
    alignContent: 'center',
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    height: theme.pxToRem(34),
    padding: theme.spacing(0, 1),
  },
  moreAttached: {
    alignContent: 'center',
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(4),
    height: theme.pxToRem(34),
    padding: theme.spacing(0, 1),
  },
  useAttachment: {
    marginRight: theme.spacing(0.5),
    paddingLeft: 0,
    paddingRight: theme.spacing(0.75),
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingTop: 0,
    paddingBottom: 0,
  },
  textContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
    minWidth: 0,
  },
  mainText: {
    color: theme.palette.text.secondary,
    textAlign: 'left',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  subText: {
    color: lighten(theme.palette.text.hint, 0.35),
    letterSpacing: theme.pxToRem(0.5),
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  pageNumber: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),

    '& > div, & input': {
      // wide enough for 3 digits + number control
      minWidth: 60,
      width: 60,
    },

    '& input': {
      padding: theme.spacing(0.5),
    },

    '& label': {
      color: lighten(theme.palette.text.hint, 0.35),
      letterSpacing: theme.pxToRem(0.5),
      margin: 0,
      paddingLeft: theme.spacing(1),
      ...theme.typography.caption,
      borderLeft: `1px solid ${theme.palette.divider}`,
    },
  },
}));

type AttachmentItemProps = {
  attachment: ReferenceOrResource;
  maxItemWidth?: number;
  /** *
   * The action to perform on the attachment.
   * - 'readonly' - The attachment can be opened to view
   * - 'read-reference' - The attachment can be opened to view, but it is not already attached.
   * - 'add' - Allow the attachment to be added.
   * - 'edit' - Allow the attachment to be deleted.
   */
  action: 'readonly' | 'add' | 'edit' | 'read-reference';
  className?: string | boolean;
  hidden?: boolean;
  disableThumbnail?: boolean;
  disableDeepLink?: boolean;
  onUseAttachment?: (attachment: ReferenceOrResource, index: number) => void;
  index?: number;
};

type KnowledgeBaseAttachmentItemProps = {
  citation: KnowledgeBaseCitation;
  attachment: ReferenceOrResource;
  className?: string | boolean;
  classes: ReturnType<typeof useStyles>;
  hidden?: boolean;
  action: 'readonly' | 'add' | 'edit' | 'read-reference';
  index?: number;
  onUseAttachment?: (attachment: ReferenceOrResource, index: number) => void;
};

const KnowledgeBaseAttachmentItem = React.forwardRef<
  HTMLDivElement,
  KnowledgeBaseAttachmentItemProps
>(
  (
    {
      citation,
      attachment,
      className,
      classes,
      hidden,
      action,
      index,
      onUseAttachment,
    },
    ref,
  ) => {
    const { formatMessage } = useIntl();
    const [csvFile, setCsvFile] = useState<OpenCsvKnowledgeBaseFile | null>(
      null,
    );
    const handleOpenCsv = useCallback((file: OpenCsvKnowledgeBaseFile) => {
      setCsvFile(file);
    }, []);
    const openKnowledgeBaseFile = useOpenKnowledgeBaseFile(citation, {
      onOpenCsv: handleOpenCsv,
    });

    const label =
      action === 'add'
        ? formatMessage(messages.insertAttachment)
        : action === 'edit'
        ? formatMessage(messages.deleteAttachment)
        : formatMessage(messages.viewAttachment);

    const Icon =
      action === 'add' ? PlusCircle : action === 'edit' ? Trash : FileText;

    const mainClickHandler = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        void openKnowledgeBaseFile();
      },
      [openKnowledgeBaseFile],
    );

    const iconClickHandler = useCallback(
      (e: React.MouseEvent) => {
        if (action !== 'add' && action !== 'edit') {
          return;
        }
        e.stopPropagation();
        onUseAttachment?.(attachment, index ?? 0);
      },
      [attachment, index, onUseAttachment, action],
    );

    return (
      <>
        <Box
          className={clsx(
            classes.badge,
            (action === 'add' || action === 'edit') && classes.action,
            className,
          )}
          gap={1}
          tabIndex={
            action === 'readonly' || action === 'read-reference' || hidden
              ? undefined
              : 0
          }
          ref={ref}
          onClick={mainClickHandler}
        >
          <Box justify="space-between" gap={0.5} align="center" width="100%">
            {(action === 'add' || action === 'edit') && (
              <Tooltip title={label}>
                <IconButton
                  icon={Icon}
                  className={classes.useAttachment}
                  onClick={iconClickHandler}
                  size={0}
                  aria-label={label}
                />
              </Tooltip>
            )}
            {(action === 'readonly' || action === 'read-reference') && (
              <Tooltip title={citation.title}>
                <Icon />
              </Tooltip>
            )}
            <Tooltip title={citation.title}>
              <Box gap={1.5} className={classes.textContainer}>
                <Text
                  className={classes.mainText}
                  size="caption"
                  aria-hidden={hidden}
                >
                  {citation.displayName}
                </Text>
                <Text
                  className={classes.subText}
                  size="caption"
                  aria-hidden={hidden}
                >
                  {citation.sourceLabel}
                </Text>
              </Box>
            </Tooltip>
          </Box>
        </Box>
        {csvFile && (
          <CsvAttachmentModal
            isOpen={true}
            onDismiss={() => setCsvFile(null)}
            url={csvFile.url}
            downloadUrl={csvFile.downloadUrl}
            fileName={csvFile.fileName}
            data-testid="kb-attachment-csv"
          />
        )}
      </>
    );
  },
);

KnowledgeBaseAttachmentItem.displayName = 'KnowledgeBaseAttachmentItem';

export const AttachmentItem = React.forwardRef<
  HTMLDivElement,
  AttachmentItemProps
>((props, ref) => {
  const {
    attachment,
    className,
    action,
    hidden,
    disableDeepLink = true,
    disableThumbnail = true,
    onUseAttachment,
    index,
  } = props;
  const { loading, attachmentMap, openAttachment } = useAttachmentArbiter();
  const classes = useStyles(props);
  const { formatMessage } = useIntl();
  const reference = (attachment as fhir.Reference).reference ?? '';
  // KB-backed references use an `s3://<bucket>/.files-kb-<acct>/<proj>/<path>`
  // shape that FHIR search can't resolve; short-circuit to a KB renderer so the
  // list item shows the file name and opens the file-service file on click.
  const knowledgeBaseCitation = parseKnowledgeBaseCitation(reference);
  const resourceType = reference.split('/').at(0);
  const attachmentData: Attachment | undefined = attachmentMap[reference];

  const Icon = useMemo(() => {
    if (action === 'add') {
      if (attachmentData) {
        return Login; // looks like "insert"
      }
      return PlusCircle;
    }
    if (!attachmentData && action !== 'read-reference') {
      return AlertTriangle;
    }
    if (action === 'edit') {
      return Trash;
    }

    switch (resourceType) {
      case 'Observation':
      case 'Procedure':
        return Helix;
      case 'QuestionnaireResponse':
        return CheckList;
      case 'Patient':
        return User;
      default:
        // You might want to add a default icon or return null
        return FileText;
    }
  }, [resourceType, attachmentData, action]);

  const label = useMemo(() => {
    if (action === 'add') {
      if (attachmentData) {
        return formatMessage(messages.insertAttachment);
      }
      return formatMessage(messages.addAttachment);
    }
    if (action === 'edit') {
      return formatMessage(messages.deleteAttachment);
    }
    return formatMessage(messages.viewAttachment);
  }, [action, attachmentData, formatMessage]);

  const mainClickHandler = useCallback(
    (e: React.MouseEvent) => {
      openAttachment(attachment, { disableDeepLink });
      e.stopPropagation();
    },
    [openAttachment, attachment, disableDeepLink],
  );

  const iconClickHandler = useCallback(
    (e: React.MouseEvent) => {
      if (action !== 'add' && action !== 'edit') {
        return;
      }
      e.stopPropagation();
      onUseAttachment?.(attachment, index ?? 0);
    },
    [attachment, index, onUseAttachment, action],
  );

  const text =
    attachmentData?.rawDisplay ??
    (isDocumentReference(attachment)
      ? attachment.type?.text
      : isObservation(attachment)
      ? new ObservationAttachment(attachment).display
      : resourceType);

  let date: string | undefined = attachmentData?.displayDate;
  if (!date) {
    const valueDateTime = isDocumentReference(attachment)
      ? attachment.created
      : isObservation(attachment)
      ? attachment.effectiveDateTime
      : undefined;
    if (valueDateTime) {
      const dateTime = DateTime.fromISO(valueDateTime);
      if (dateTime.isValid) {
        date = dateTime.toFormat('LLL yy');
      }
    } else {
      date = '';
    }
  }

  const [pageNumber, setPageNumber] = useState(
    (attachment as fhir.DocumentReference).extension?.find(
      ext => ext.url === PAGE_NUMBER_EXTENSION_URL,
    )?.valueInteger ?? 1,
  );

  if (knowledgeBaseCitation) {
    return (
      <KnowledgeBaseAttachmentItem
        ref={ref}
        citation={knowledgeBaseCitation}
        attachment={attachment}
        className={className}
        classes={classes}
        hidden={hidden}
        action={action}
        index={index}
        onUseAttachment={onUseAttachment}
      />
    );
  }

  let content = (
    <Box gap={1.5} className={classes.textContainer}>
      <Text className={classes.mainText} size="caption" aria-hidden={hidden}>
        {text}
      </Text>
      <Text className={classes.subText} size="caption" aria-hidden={hidden}>
        {date}
      </Text>
      {resourceType === 'DocumentReference' && action === 'edit' && (
        <TextField
          type="number"
          className={classes.pageNumber}
          label={formatMessage(messages.pageNumber)}
          onClick={e => e.stopPropagation()}
          value={pageNumber}
          onChange={e => {
            const extension = (
              attachment as fhir.DocumentReference
            ).extension?.find(ext => ext.url === PAGE_NUMBER_EXTENSION_URL);
            if (extension) {
              extension.valueInteger = parseInt(e.target.value);
            } else {
              (attachment as fhir.DocumentReference).extension?.push({
                url: PAGE_NUMBER_EXTENSION_URL,
                valueInteger: parseInt(e.target.value),
              });
            }
            setPageNumber(parseInt(e.target.value));
          }}
        />
      )}
    </Box>
  );

  if (
    attachmentData instanceof DocumentReferenceAttachment &&
    attachmentData.isImage() &&
    !disableThumbnail
  )
    content = (
      <DocumentReferenceContent
        attachment={attachmentData}
        reference={attachment}
        disablePdfNav={true}
      />
    );

  return loading ? (
    <Skeleton animation="shine" width={60} height={34} />
  ) : (
    <Box
      className={clsx(
        classes.badge,
        (action === 'add' || action === 'edit') && classes.action,
        className,
      )}
      gap={1}
      tabIndex={
        action === 'readonly' || action === 'read-reference' || hidden
          ? undefined
          : 0
      }
      ref={ref}
      onClick={mainClickHandler}
    >
      <Box justify="space-between" gap={0.5} align="center" width="100%">
        {(action === 'add' || action === 'edit') && (
          <Tooltip title={label}>
            <IconButton
              icon={Icon}
              className={classes.useAttachment}
              onClick={iconClickHandler}
              size={0}
              aria-label={label}
            />
          </Tooltip>
        )}
        {(action === 'readonly' || action === 'read-reference') && (
          <Tooltip
            title={
              attachmentData
                ? formatMessage(messages.viewAttachment)
                : formatMessage(messages.attachmentNotFound)
            }
          >
            <Icon />
          </Tooltip>
        )}
        <Tooltip
          title={
            action === 'add'
              ? formatMessage(messages.addAttachment)
              : attachmentData
              ? formatMessage(messages.viewAttachment)
              : formatMessage(messages.attachmentNotFound)
          }
        >
          {content}
        </Tooltip>
      </Box>
    </Box>
  );
});

export const AttachmentsPopover = ({
  allAttachments,
  onUseAttachment,
  action,
  isLoading,
  anchorElement,
  placement,
}: {
  allAttachments: ReferenceOrResource[];
  onUseAttachment: (reference: ReferenceOrResource, index: number) => void;
  action: 'readonly' | 'add' | 'read-reference';
  isLoading: boolean;
  anchorElement?: React.ReactNode;
  placement?:
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'right-start'
    | 'right'
    | 'right-end'
    | 'bottom-end'
    | 'bottom'
    | 'bottom-start'
    | 'left-end'
    | 'left'
    | 'left-start';
}) => {
  const classes = useStyles({ action });

  const [search, setSearch] = useState('');

  const { attachmentMap } = useAttachmentArbiter();

  const filteredAttachments = useMemo(() => {
    const attachments = _uniqBy(
      allAttachments as fhir.Reference[],
      a => a.reference ?? a.id,
    ) as ReferenceOrResource[];

    if (!search) return attachments;

    // this cast is necessary in TS 4.x but fixed in 5.x
    return (attachments as fhir.Reference[])?.filter(
      attachment =>
        (isFhirReference(attachment) &&
          attachmentMap[attachment.reference!]?.rawDisplay
            ?.toLowerCase()
            .includes(search.toLowerCase())) ||
        (isDocumentReference(attachment) &&
          attachment.type?.text
            ?.toLowerCase()
            .includes(search.toLowerCase())) ||
        (isObservation(attachment) &&
          new ObservationAttachment(attachment).display
            ?.toLowerCase()
            .includes(search.toLowerCase())),
    );
  }, [allAttachments, search, attachmentMap]);
  return (
    <Popover
      className={classes.popover}
      aria-label="Attachments popover"
      placement={placement}
      anchorElement={
        anchorElement ?? (
          <IconButton
            aria-label="Attachments"
            icon={Paperclip}
            size={0}
            color="inverse"
          />
        )
      }
    >
      {isLoading ? (
        <Skeleton animation="shine" width="100%" height="100%" />
      ) : (
        <>
          <Box className={classes.searchHeader}>
            <SearchField
              fullWidth
              aria-label="Search attachments"
              placeholder="Search"
              onChange={e => setSearch(e.target.value)}
            />
          </Box>
          <Box className={classes.results} direction="column" gap={1}>
            {filteredAttachments?.length === 0 ? (
              <Box
                className={classes.emptyState}
                align="center"
                justify="center"
                fullWidth
                fullHeight
                direction="column"
                gap={0.75}
              >
                <Search />
                <Text
                  className={classes.emptyText}
                  size="subbody"
                  align="center"
                >
                  No results
                </Text>
              </Box>
            ) : (
              (filteredAttachments ?? []).map((attachment, index) => (
                <AttachmentItem
                  key={attachment.id ?? index}
                  className={classes.attachmentItem}
                  attachment={attachment}
                  action={action}
                  onUseAttachment={att => onUseAttachment?.(att, index)}
                  index={index}
                />
              ))
            )}
          </Box>
        </>
      )}
    </Popover>
  );
};

export const AddAttachmentPopover = ({
  patient,
  onUseAttachment,
  anchorElement,
  action,
  placement,
}: {
  patient: fhir.Patient;
  onUseAttachment: (reference: ReferenceOrResource, index: number) => void;
  anchorElement?: React.ReactNode;
  action: 'add' | 'read-reference';
  placement?:
    | 'top-start'
    | 'top'
    | 'top-end'
    | 'right-start'
    | 'right'
    | 'right-end'
    | 'bottom-end'
    | 'bottom'
    | 'bottom-start'
    | 'left-end'
    | 'left'
    | 'left-start';
}) => {
  const classes = useStyles({ action });

  const { isLoading, searchResults, loadMore, hasMoreToLoad } =
    usePatientViewerFhirSearch({
      searchConfig: {
        dataSource: 'DOCUMENT_REFERENCE_DATA_SOURCE_TYPE',
        patientId: patient.id!,
        timeFilterProps: {
          DocumentReference: 'created',
        },
        deviceId: undefined,
        useTagFilter: true,
        tagFilters: [
          {
            system: 'http://lifeomic.com/fhir/source',
            code: 'LifeOmic EHR',
          },
        ],
      },
      inMemoryPaginationPageSize: 100,
    });

  const { data: dimensionData } = useFetchDimensionData({
    patientId: patient.id!,
  });

  // maybe change this to do server side searching. 750 is double the highest
  // number of documents we've seen on a single patient.
  useEffect(() => {
    if (hasMoreToLoad && !isLoading && searchResults.length < 750) {
      loadMore();
    }
  }, [hasMoreToLoad, loadMore, isLoading, searchResults.length]);

  const anchor = anchorElement ?? (
    <Button
      icon={PlusCircle}
      className={classes.addAttachment}
      variant="outlined"
      aria-label="Add Attachment"
    >
      Add
    </Button>
  );

  const results = useMemo(() => {
    const documentResults = searchResults
      .flatMap(r => r)
      .flatMap(r => r.results)
      .map(r => r.resource) as fhir.DocumentReference[];

    const dimensionResults = dimensionData?.flatMap(d => d.dimension) ?? [];
    const resources = dimensionResults
      .map(d => d.resources[0])
      .filter(r => r !== undefined);

    return [...documentResults, ...resources];
  }, [searchResults, dimensionData]);

  return (
    <AttachmentsPopover
      anchorElement={anchor}
      allAttachments={results}
      isLoading={isLoading}
      onUseAttachment={onUseAttachment}
      action={action}
      placement={placement}
    />
  );
};

export const AttachmentList = (props: AttachmentListProps) => {
  const { attachments: attachmentsIn, onUseAttachment, action } = props;
  const classes = useStyles({
    disableThumbnail: true,
    maxItemWidth: props.maxItemWidth,
    action,
  });
  const sizes = useRef<number[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const attachments = _uniqBy(attachmentsIn, 'reference');
  const [maxVisibleIndex, setMaxVisibleIndex] = useState(attachments.length);
  const { loading, addAttachment, attachmentMap } = useAttachmentArbiter();

  const { formatMessage } = useIntl();

  const onAddAttachment = (newReference: fhir.Reference | fhir.Resource) => {
    const reference = props.activeImpression.addAttachment(newReference);
    if (!reference) {
      console.error('Failed to add attachment', newReference);
      return;
    }
    const existingAttachment = reference.reference
      ? attachmentMap[reference.reference]
      : undefined;
    if (existingAttachment) {
      onUseAttachment(reference, props.activeImpression.attachments.length + 1);
    } else {
      const resourceType = reference.reference?.split('/').at(0);
      const resource =
        resourceType === 'Observation'
          ? new ObservationAttachment(newReference as fhir.Observation)
          : new TemporaryAttachment(
              reference,
              (newReference as fhir.DocumentReference).type?.text ?? 'untitled',
            );
      addAttachment(reference.reference!, resource);
      onUseAttachment(reference, props.activeImpression.attachments.length + 1);
    }
  };

  useEffect(() => {
    const container = containerRef.current;

    if (!container) return;

    const updateMaxVisible = () => {
      let containerWidth =
        container.offsetWidth - PLUS_N_TEXT_WIDTH - ADD_ATTACHMENT_WIDTH;
      let index = 0;
      while (containerWidth > 0 && index < attachments.length) {
        const itemSize = sizes.current[index] ?? 0;
        if (itemSize < containerWidth) {
          containerWidth -= itemSize;
          index++;
        } else {
          containerWidth = 0;
        }

        containerWidth -= GAP_SIZE;
      }

      // Ensure at least one item is displayed
      setMaxVisibleIndex(Math.max(index - 1, 0));
    };

    const resizeObserver = new ResizeObserver(updateMaxVisible);
    resizeObserver.observe(container);

    updateMaxVisible();

    return () => resizeObserver.disconnect();
  }, [attachments.length, loading]);

  return (
    <Box fullWidth ref={containerRef} data-testid="es-attachments">
      <Box gap={0.75} className={classes.container}>
        {props.action !== 'readonly' && (
          <AddAttachmentPopover
            action={props.action}
            patient={props.patient}
            onUseAttachment={onAddAttachment}
          />
        )}
        {attachments.map((attachment, index) => (
          <Box
            align="center"
            key={attachment.id ?? index}
            gap={1}
            data-testid={`es-attachments-item-${attachment.id ?? index}`}
          >
            <AttachmentItem
              {...props}
              attachment={attachment}
              hidden={index > maxVisibleIndex}
              className={
                (index > maxVisibleIndex && classes.visuallyHidden) ||
                classes.border
              }
              ref={item => (sizes.current[index] = item?.offsetWidth ?? 0)}
              onUseAttachment={onUseAttachment}
              index={index}
              action={props.action}
            />

            {index === maxVisibleIndex &&
              attachments.length > maxVisibleIndex + 1 && (
                <AttachmentsPopover
                  anchorElement={
                    <Text
                      className={classes.moreAttached}
                      data-testid="es-attachments-show-more"
                    >
                      {formatMessage(messages.extraAttachmentsCount, {
                        count: attachments.slice(maxVisibleIndex + 1).length,
                      })}
                    </Text>
                  }
                  allAttachments={attachments.slice(maxVisibleIndex + 1)}
                  isLoading={false}
                  onUseAttachment={onUseAttachment}
                  action={props.action}
                />
              )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
